<template>
    <app-layout>
        <template #icon>
            <div class="h-10 w-10 flex items-center justify-center">
                <WorkflowsHeaderLogo />
            </div>
        </template>
        <template #title> {{ $t("workflows.title") }} </template>
        <template #description> {{ $t("workflows.description") }}</template>
        <template #actions>
            <CreateWorkflowModal :integrations="integrations">
                <AddButton
                    feature="custom_workflow"
                    data-cy="create-new-workflow"
                >
                    {{ $t("workflows.new_workflow") }}</AddButton
                >
            </CreateWorkflowModal>
        </template>

        <div class="px-4 pt-4 sm:px-6">
            <div class="mb-4 gap-3 flex w-full items-center justify-end">
                <div class="max-w-md relative w-full">
                    <input
                        v-model="searchQuery"
                        type="text"
                        :placeholder="$t('workflows.search_placeholder')"
                        class="rounded-md border-iron-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-z-primary-500 focus:ring-z-primary-500 w-full border focus:ring-1 focus:outline-none"
                    />
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        class="right-3 top-2.5 h-4 w-4 text-iron-400 absolute"
                    >
                        <circle cx="11" cy="11" r="8" />
                        <path d="m21 21-4.35-4.35" />
                    </svg>
                </div>
            </div>

            <!-- Smart Workflow Recommendations -->
            <div v-if="smartRecommendations.length" class="mb-6">
                <h3 class="mb-3 text-sm font-medium text-iron-900">
                    {{ $t("workflows.smart_recommendations.title") }}
                </h3>
                <div class="gap-3 sm:grid-cols-2 lg:grid-cols-3 grid">
                    <div
                        v-for="recommendation in smartRecommendations"
                        :key="recommendation.id"
                        class="group rounded-lg border-iron-200 from-blue-50 to-indigo-50 p-4 hover:border-blue-300 hover:shadow-md relative cursor-pointer border bg-gradient-to-br transition-all"
                        @click="
                            !hasSubscription
                                ? openSubscriptionModal(recommendation.feature)
                                : createSmartWorkflow(recommendation)
                        "
                    >
                        <!-- Pro Lock Icon -->
                        <div
                            v-if="!hasSubscription"
                            class="-right-1 -top-1 h-5 w-5 bg-white shadow-md absolute z-50 flex items-center justify-center rounded-full transition-all duration-150 group-hover:scale-110"
                        >
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M3.33319 13.3332H12.6665M1.33319 2.6665L3.33319 10.6665H12.6665L14.6665 2.6665L10.6665 7.33317L7.99986 2.6665L5.33319 7.33317L1.33319 2.6665Z"
                                    stroke="url(#paint0_linear_1067_2699)"
                                    stroke-width="1.4"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                                <defs>
                                    <linearGradient
                                        id="paint0_linear_1067_2699"
                                        x1="1.00018"
                                        y1="1.99976"
                                        x2="10.1364"
                                        y2="14.5624"
                                        gradientUnits="userSpaceOnUse"
                                    >
                                        <stop stop-color="#FF8D47" />
                                        <stop
                                            offset="0.329612"
                                            stop-color="#FF8947"
                                        />
                                        <stop
                                            offset="0.534043"
                                            stop-color="#FF4247"
                                        />
                                        <stop
                                            offset="0.68692"
                                            stop-color="#E92A5B"
                                        />
                                        <stop
                                            offset="0.828171"
                                            stop-color="#DD1C67"
                                        />
                                        <stop offset="1" stop-color="#CF0283" />
                                    </linearGradient>
                                </defs>
                            </svg>
                        </div>
                        <div class="gap-3 flex items-start">
                            <div
                                class="bg-blue-100 p-2 group-hover:bg-blue-200 rounded-full"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    class="h-4 w-4 text-blue-600"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        d="M13 10V3L4 14h7v7l9-11h-7z"
                                    />
                                </svg>
                            </div>
                            <div class="flex-1">
                                <h4 class="text-sm font-semibold text-iron-900">
                                    {{ recommendation.title }}
                                </h4>
                                <p class="mt-1 text-xs text-iron-600">
                                    {{ recommendation.description }}
                                </p>
                                <div
                                    class="mt-2 gap-1 text-xs text-blue-600 flex items-center"
                                >
                                    <span
                                        >{{ recommendation.workflows.length }}
                                        {{
                                            $t(
                                                "workflows.workflows_count",
                                                recommendation.workflows.length,
                                            )
                                        }}</span
                                    >
                                    <svg
                                        class="h-3 w-3"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                    >
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            stroke-width="2"
                                            d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                                        />
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="space-y-4">
                <div
                    class="rounded-lg border-iron-200 bg-white shadow-sm overflow-hidden border"
                >
                    <div
                        class="border-iron-200 bg-iron-50 px-4 py-3 hover:bg-iron-100 flex cursor-pointer items-center justify-between border-b"
                        @click="toggleGroup('reservation_made')"
                    >
                        <span class="text-sm font-medium text-iron-900">{{
                            $t("workflows.steps.when_reservation_is_made")
                        }}</span>
                        <div class="gap-2 flex items-center">
                            <span
                                class="bg-iron-200 px-2 py-1 text-xs font-medium text-iron-700 rounded-full"
                                >{{ groupedWorkflows.reservation_made.length }}
                                {{
                                    $t(
                                        "workflows.workflows_count",
                                        groupedWorkflows.reservation_made
                                            .length,
                                    )
                                }}</span
                            >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                class="h-4 w-4 transform transition-transform duration-200"
                                :class="{
                                    'rotate-180':
                                        openGroups.has('reservation_made'),
                                }"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M19 9l-7 7-7-7"
                                />
                            </svg>
                        </div>
                    </div>
                    <div v-if="openGroups.has('reservation_made')" class="p-4">
                        <template
                            v-if="groupedWorkflows.reservation_made.length"
                        >
                            <workflow
                                v-for="workflow in groupedWorkflows.reservation_made"
                                :key="workflow.id"
                                :workflow="workflow"
                                :event-types="eventTypes"
                                :personal-event-types="personalEventTypes"
                                :team-event-types="teamEventTypes"
                                :can-assign-team-workflows="
                                    canAssignTeamWorkflows
                                "
                            />
                        </template>
                        <template v-else>
                            <div
                                class="rounded-xl border-iron-200 bg-white py-14 shadow-sm flex flex-col items-center justify-center border text-center"
                            >
                                <div class="mt-6 font-medium text-iron-500">
                                    {{
                                        $t("workflows.no_workflows_for_trigger")
                                    }}
                                </div>
                                <CreateWorkflowModal
                                    :integrations="integrations"
                                    :preselected-trigger="'reservation_made'"
                                >
                                    <button
                                        type="button"
                                        class="mt-6 rounded-md border-iron-300 bg-white px-3 py-2 text-sm font-medium text-iron-900 shadow-sm hover:bg-iron-50 inline-flex items-center border"
                                    >
                                        <span class="mr-2">+</span
                                        >{{
                                            $t("workflows.new_workflow_button")
                                        }}
                                    </button>
                                </CreateWorkflowModal>
                            </div>
                        </template>
                    </div>
                </div>

                <div
                    class="rounded-lg border-iron-200 bg-white shadow-sm overflow-hidden border"
                >
                    <div
                        class="border-iron-200 bg-iron-50 px-4 py-3 hover:bg-iron-100 flex cursor-pointer items-center justify-between border-b"
                        @click="toggleGroup('reservation_rescheduled')"
                    >
                        <span class="text-sm font-medium text-iron-900">{{
                            $t(
                                "workflows.steps.when_reservation_is_rescheduled",
                            )
                        }}</span>
                        <div class="gap-2 flex items-center">
                            <span
                                class="bg-iron-200 px-2 py-1 text-xs font-medium text-iron-700 rounded-full"
                                >{{
                                    groupedWorkflows.reservation_rescheduled
                                        .length
                                }}
                                {{
                                    $t(
                                        "workflows.workflows_count",
                                        groupedWorkflows.reservation_rescheduled
                                            .length,
                                    )
                                }}</span
                            >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                class="h-4 w-4 transform transition-transform duration-200"
                                :class="{
                                    'rotate-180': openGroups.has(
                                        'reservation_rescheduled',
                                    ),
                                }"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M19 9l-7 7-7-7"
                                />
                            </svg>
                        </div>
                    </div>
                    <div
                        v-if="openGroups.has('reservation_rescheduled')"
                        class="p-4"
                    >
                        <template
                            v-if="
                                groupedWorkflows.reservation_rescheduled.length
                            "
                        >
                            <workflow
                                v-for="workflow in groupedWorkflows.reservation_rescheduled"
                                :key="workflow.id"
                                :workflow="workflow"
                                :event-types="eventTypes"
                                :personal-event-types="personalEventTypes"
                                :team-event-types="teamEventTypes"
                                :can-assign-team-workflows="
                                    canAssignTeamWorkflows
                                "
                            />
                        </template>
                        <template v-else>
                            <div
                                class="rounded-xl border-iron-200 bg-white py-14 shadow-sm flex flex-col items-center justify-center border text-center"
                            >
                                <div class="mt-6 font-medium text-iron-500">
                                    {{
                                        $t("workflows.no_workflows_for_trigger")
                                    }}
                                </div>
                                <CreateWorkflowModal
                                    :integrations="integrations"
                                    :preselected-trigger="'reservation_rescheduled'"
                                >
                                    <button
                                        type="button"
                                        class="mt-6 rounded-md border-iron-300 bg-white px-3 py-2 text-sm font-medium text-iron-900 shadow-sm hover:bg-iron-50 inline-flex items-center border"
                                    >
                                        <span class="mr-2">+</span
                                        >{{
                                            $t("workflows.new_workflow_button")
                                        }}
                                    </button>
                                </CreateWorkflowModal>
                            </div>
                        </template>
                    </div>
                </div>

                <div
                    class="rounded-lg border-iron-200 bg-white shadow-sm overflow-hidden border"
                >
                    <div
                        class="border-iron-200 bg-iron-50 px-4 py-3 hover:bg-iron-100 flex cursor-pointer items-center justify-between border-b"
                        @click="toggleGroup('reservation_canceled')"
                    >
                        <span class="text-sm font-medium text-iron-900">{{
                            $t("workflows.steps.when_reservation_is_canceled")
                        }}</span>
                        <div class="gap-2 flex items-center">
                            <span
                                class="bg-iron-200 px-2 py-1 text-xs font-medium text-iron-700 rounded-full"
                                >{{
                                    groupedWorkflows.reservation_canceled.length
                                }}
                                {{
                                    $t(
                                        "workflows.workflows_count",
                                        groupedWorkflows.reservation_canceled
                                            .length,
                                    )
                                }}</span
                            >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                class="h-4 w-4 transform transition-transform duration-200"
                                :class="{
                                    'rotate-180': openGroups.has(
                                        'reservation_canceled',
                                    ),
                                }"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M19 9l-7 7-7-7"
                                />
                            </svg>
                        </div>
                    </div>
                    <div
                        v-if="openGroups.has('reservation_canceled')"
                        class="p-4"
                    >
                        <template
                            v-if="groupedWorkflows.reservation_canceled.length"
                        >
                            <workflow
                                v-for="workflow in groupedWorkflows.reservation_canceled"
                                :key="workflow.id"
                                :workflow="workflow"
                                :event-types="eventTypes"
                                :personal-event-types="personalEventTypes"
                                :team-event-types="teamEventTypes"
                                :can-assign-team-workflows="
                                    canAssignTeamWorkflows
                                "
                            />
                        </template>
                        <template v-else>
                            <div
                                class="rounded-xl border-iron-200 bg-white py-14 shadow-sm flex flex-col items-center justify-center border text-center"
                            >
                                <div class="mt-6 font-medium text-iron-500">
                                    {{
                                        $t("workflows.no_workflows_for_trigger")
                                    }}
                                </div>
                                <CreateWorkflowModal
                                    :integrations="integrations"
                                    :preselected-trigger="'reservation_canceled'"
                                >
                                    <button
                                        type="button"
                                        class="mt-6 rounded-md border-iron-300 bg-white px-3 py-2 text-sm font-medium text-iron-900 shadow-sm hover:bg-iron-50 inline-flex items-center border"
                                    >
                                        <span class="mr-2">+</span
                                        >{{
                                            $t("workflows.new_workflow_button")
                                        }}
                                    </button>
                                </CreateWorkflowModal>
                            </div>
                        </template>
                    </div>
                </div>

                <div
                    class="rounded-lg border-iron-200 bg-white shadow-sm overflow-hidden border"
                >
                    <div
                        class="border-iron-200 bg-iron-50 px-4 py-3 hover:bg-iron-100 flex cursor-pointer items-center justify-between border-b"
                        @click="toggleGroup('others')"
                    >
                        <span class="text-sm font-medium text-iron-900">{{
                            $t(
                                "workflows.smart_recommendations.reminders.title",
                            )
                        }}</span>
                        <div class="gap-2 flex items-center">
                            <span
                                class="bg-iron-200 px-2 py-1 text-xs font-medium text-iron-700 rounded-full"
                                >{{ groupedWorkflows.others.length }}
                                {{
                                    $t(
                                        "workflows.workflows_count",
                                        groupedWorkflows.others.length,
                                    )
                                }}</span
                            >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                class="h-4 w-4 transform transition-transform duration-200"
                                :class="{
                                    'rotate-180': openGroups.has('others'),
                                }"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M19 9l-7 7-7-7"
                                />
                            </svg>
                        </div>
                    </div>
                    <div v-if="openGroups.has('others')" class="p-4">
                        <template v-if="groupedWorkflows.others.length">
                            <workflow
                                v-for="workflow in groupedWorkflows.others"
                                :key="workflow.id"
                                :workflow="workflow"
                                :event-types="eventTypes"
                                :personal-event-types="personalEventTypes"
                                :team-event-types="teamEventTypes"
                                :can-assign-team-workflows="
                                    canAssignTeamWorkflows
                                "
                            />
                        </template>
                        <template v-else>
                            <div
                                class="rounded-xl border-iron-200 bg-white py-14 shadow-sm flex flex-col items-center justify-center border text-center"
                            >
                                <div class="mt-6 font-medium text-iron-500">
                                    {{
                                        $t("workflows.no_workflows_for_trigger")
                                    }}
                                </div>
                                <CreateWorkflowModal
                                    :integrations="integrations"
                                    :preselected-trigger="'reservation_will_happen_in'"
                                >
                                    <button
                                        type="button"
                                        class="mt-6 rounded-md border-iron-300 bg-white px-3 py-2 text-sm font-medium text-iron-900 shadow-sm hover:bg-iron-50 inline-flex items-center border"
                                    >
                                        <span class="mr-2">+</span
                                        >{{
                                            $t("workflows.new_workflow_button")
                                        }}
                                    </button>
                                </CreateWorkflowModal>
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </app-layout>
</template>

<script lang="ts" setup>
import AppLayout from "../../../../Layouts/AppLayout.vue";
import { computed, ref } from "vue";
import AddButton from "../Atomic/AddButton.vue";
import CreateWorkflowModal from "./components/CreateWorkflowModal.vue";
import Workflow from "./components/Workflow.vue";
import type { Workflow as WorkflowType } from "./components/Workflow.vue";
import { PropType } from "vue";
import WorkflowsHeaderLogo from "./components/WorklowsHeaderLogo.svg";
import { useI18n } from "vue-i18n";
import { router } from "@inertiajs/vue3";
import { useHasSubscription } from "@/composables/useHasSubscription";
import useProDialog from "@/ProModal/composables/useProDialog";

const props = defineProps({
    workflows: {
        type: Array as PropType<WorkflowType[]>,
        required: true,
    },
    eventTypes: {
        type: Array,
        required: true,
    },
    personalEventTypes: {
        type: Array,
        required: false,
        default: () => [],
    },
    teamEventTypes: {
        type: Array,
        required: false,
        default: () => [],
    },
    integrations: {
        type: Array,
        required: true,
    },
    canAssignTeamWorkflows: {
        type: Boolean,
        required: false,
        default: false,
    },
});

const { t } = useI18n();
const { hasSubscription } = useHasSubscription();
const proDialog = useProDialog();

const integrationFilterOptions = computed(() => {
    const labelByType: Record<string, string> = {
        EMAIL: "Email",
        ECOMAIL: "Ecomail",
        FREELO: "Freelo",
        HUBSPOT: "HubSpot",
        PIPEDRIVE: "Pipedrive",
        RAYNET: "Raynet",
        WEBHOOK: "Webhook",
        CLOSE: "Close",
        SLACK: "Slack",
    };

    const types = new Set<string>();
    (props.workflows || []).forEach((w: WorkflowType) => {
        const t = integrationTypeOf(w);
        if (t) types.add(t);
    });

    const options = Array.from(types)
        .map((type) => ({ value: type, label: labelByType[type] ?? type }))
        .sort((a, b) => a.label.localeCompare(b.label));

    // Always include Email option (not a real Integration, but an action)
    if (!types.has("EMAIL")) {
        options.unshift({ value: "EMAIL", label: "Email" });
    }
    return options;
});

const selectedIntegration = ref<string>("ALL");
const searchQuery = ref<string>("");

const integrationTypeOf = (workflow: WorkflowType): string | null => {
    switch (workflow.action) {
        case "send_email":
            return "EMAIL";
        case "add_task_to_freelo":
            return "FREELO";
        case "add_contact_to_ecomail":
            return "ECOMAIL";
        case "add_person_to_pipedrive":
            return "PIPEDRIVE";
        case "add_lead_to_raynet":
            return "RAYNET";
        case "add_contact_to_hubspot":
            return "HUBSPOT";
        case "sync_to_close":
            return "CLOSE";
        case "send_to_slack":
            return "SLACK";
        case "send_webhook":
            return "WEBHOOK";
        default:
            return null;
    }
};

const filteredWorkflows = computed<WorkflowType[]>(() => {
    let workflows = props.workflows || [];

    // Filter by integration type
    if (selectedIntegration.value !== "ALL") {
        workflows = workflows.filter(
            (w) => integrationTypeOf(w) === selectedIntegration.value,
        );
    }

    // Filter by search query
    if (searchQuery.value.trim()) {
        const query = searchQuery.value.toLowerCase();
        workflows = workflows.filter((w) => {
            const workflowName = w.name?.toLowerCase() || "";
            const integrationType = integrationTypeOf(w)?.toLowerCase() || "";
            const integrationLabel =
                getIntegrationLabel(integrationType)?.toLowerCase() || "";

            return (
                workflowName.includes(query) ||
                integrationType.includes(query) ||
                integrationLabel.includes(query)
            );
        });
    }

    return workflows;
});

const getIntegrationLabel = (type: string | null): string | null => {
    if (!type) return null;
    const map: Record<string, string> = {
        EMAIL: "Email",
        ECOMAIL: "Ecomail",
        FREELO: "Freelo",
        HUBSPOT: "HubSpot",
        PIPEDRIVE: "Pipedrive",
        RAYNET: "Raynet",
        WEBHOOK: "Webhook",
        CLOSE: "Close",
        SLACK: "Slack",
    };
    return map[type] ?? type;
};

const groupedWorkflows = computed(() => {
    const groups = {
        reservation_made: [] as WorkflowType[],
        reservation_rescheduled: [] as WorkflowType[],
        reservation_canceled: [] as WorkflowType[],
        others: [] as WorkflowType[],
    };

    for (const w of filteredWorkflows.value) {
        if (w.trigger === "reservation_made") groups.reservation_made.push(w);
        else if (w.trigger === "reservation_rescheduled")
            groups.reservation_rescheduled.push(w);
        else if (w.trigger === "reservation_canceled")
            groups.reservation_canceled.push(w);
        else groups.others.push(w);
    }

    // Put defaults on top of each group
    for (const key of Object.keys(groups) as Array<keyof typeof groups>) {
        groups[key] = [
            ...groups[key].filter((w) => w.default),
            ...groups[key].filter((w) => !w.default),
        ];
    }

    // Auto-open groups that have search results
    if (searchQuery.value.trim()) {
        const newOpenGroups = new Set<string>();
        if (groups.reservation_made.length)
            newOpenGroups.add("reservation_made");
        if (groups.reservation_rescheduled.length)
            newOpenGroups.add("reservation_rescheduled");
        if (groups.reservation_canceled.length)
            newOpenGroups.add("reservation_canceled");
        if (groups.others.length) newOpenGroups.add("others");
        openGroups.value = newOpenGroups;
    }

    return groups;
});

// Custom collapsible state management
const openGroups = ref(new Set(["reservation_made"]));

const toggleGroup = (groupName: string) => {
    if (openGroups.value.has(groupName)) {
        openGroups.value.delete(groupName);
    } else {
        openGroups.value.add(groupName);
    }
};

// Smart workflow recommendations
const smartRecommendations = computed(() => {
    const existingWorkflows = props.workflows || [];
    const recommendations = [];

    // Check if user has email reminders
    const hasReminderAttendee = existingWorkflows.some(
        (w) =>
            w.trigger === "reservation_will_happen_in" &&
            w.target === "attendees" &&
            w.action === "send_email",
    );
    const hasReminderHost = existingWorkflows.some(
        (w) =>
            w.trigger === "reservation_will_happen_in" &&
            w.target === "host" &&
            w.action === "send_email",
    );

    if (!hasReminderAttendee || !hasReminderHost) {
        recommendations.push({
            id: "email-reminders",
            title: t("workflows.smart_recommendations.email_reminders.title"),
            description: t(
                "workflows.smart_recommendations.email_reminders.description",
            ),
            feature: "custom_workflow",
            workflows: [
                {
                    trigger: "reservation_will_happen_in",
                    target: "attendees",
                    action: "send_email",
                    name: t(
                        "workflows.smart_recommendations.email_reminders.attendee_reminder",
                    ),
                },
                {
                    trigger: "reservation_will_happen_in",
                    target: "host",
                    action: "send_email",
                    name: t(
                        "workflows.smart_recommendations.email_reminders.host_reminder",
                    ),
                },
            ].filter(
                (w) =>
                    !existingWorkflows.some(
                        (ew) =>
                            ew.trigger === w.trigger &&
                            ew.target === w.target &&
                            ew.action === w.action,
                    ),
            ),
        });
    }

    // Check if user has reschedule emails
    const hasRescheduleAttendee = existingWorkflows.some(
        (w) =>
            w.trigger === "reservation_rescheduled" &&
            w.target === "attendees" &&
            w.action === "send_email",
    );
    const hasRescheduleHost = existingWorkflows.some(
        (w) =>
            w.trigger === "reservation_rescheduled" &&
            w.target === "host" &&
            w.action === "send_email",
    );

    if (!hasRescheduleAttendee || !hasRescheduleHost) {
        recommendations.push({
            id: "reschedule-emails",
            title: t(
                "workflows.smart_recommendations.reschedule_notifications.title",
            ),
            description: t(
                "workflows.smart_recommendations.reschedule_notifications.description",
            ),
            feature: "custom_workflow",
            workflows: [
                {
                    trigger: "reservation_rescheduled",
                    target: "attendees",
                    action: "send_email",
                    name: t(
                        "workflows.smart_recommendations.reschedule_notifications.attendee_reschedule_email",
                    ),
                },
                {
                    trigger: "reservation_rescheduled",
                    target: "host",
                    action: "send_email",
                    name: t(
                        "workflows.smart_recommendations.reschedule_notifications.host_reschedule_email",
                    ),
                },
            ].filter(
                (w) =>
                    !existingWorkflows.some(
                        (ew) =>
                            ew.trigger === w.trigger &&
                            ew.target === w.target &&
                            ew.action === w.action,
                    ),
            ),
        });
    }

    return recommendations.filter((r) => r.workflows.length > 0);
});

const createSmartWorkflow = (recommendation: any) => {
    try {
        for (const workflow of recommendation.workflows) {
            router.post(
                "/workflows",
                {
                    name: workflow.name,
                    trigger: workflow.trigger,
                    action: workflow.action,
                    target: workflow.target,
                    timeBefore:
                        workflow.trigger === "reservation_will_happen_in"
                            ? 3600
                            : null, // 1 hour default
                },
                {
                    preserveState: true,
                    preserveScroll: true,
                },
            );
        }
    } catch (error) {
        console.error("Failed to create smart workflow:", error);
    }
};

function openSubscriptionModal(feature?: string) {
    proDialog.show({
        feature: feature || "custom_workflow",
    });
}
</script>
